/**
 * PostHog identify/reset helpers.
 *
 * Mirrors the GA4 identifier lifecycle (SPEC-027 D2): identify the person on
 * login, reset on logout, so events are attributed correctly. Every call
 * no-ops until PostHog has actually initialized (dev builds, or PROD_ENV off,
 * or the key absent), so importing this module is always safe.
 */
import posthog from 'posthog-js'

// posthog.__loaded is set to true once posthog.init has run. Calling identify
// or reset before then just logs a warning, so we guard on it to stay quiet.
// eslint-disable-next-line no-underscore-dangle
const isReady = () => posthog && posthog.__loaded

// Whether PostHog initialised at all (a key was configured): without it no
// flag will ever arrive, so a page waiting for flags needn't wait.
export const isPosthogReady = () => Boolean(isReady())

// Whether PostHog has already said there is nothing to fetch: posthog-js
// loads the project's remote config first (array/<token>/config.js, which
// leaves it on window._POSTHOG_REMOTE_CONFIG) and only requests /flags when
// that config reports active flags. With none active (an experiment paused
// or not yet launched) /flags is never requested and onFeatureFlags never
// fires, so a page waiting for flags would wait for its timeout; this lets
// it stop as soon as the config has answered. Unknown (config not loaded,
// or loaded through the JSON fallback) reads as false.
//
// window._POSTHOG_REMOTE_CONFIG is a posthog-js internal (1.396): should an
// upgrade stop filling it, this stays false and the page only falls back to
// its timeout — never to a wrong variant, since the variant itself always
// comes from getFeatureFlag.
export const remoteConfigHasNoFlags = (
  win = typeof window !== 'undefined' ? window : {}
) => {
  if (!isReady()) return false
  // eslint-disable-next-line no-underscore-dangle
  const configs = win._POSTHOG_REMOTE_CONFIG
  const token = posthog.config && posthog.config.token
  const config = configs && token && configs[token] && configs[token].config
  return Boolean(config) && config.hasFeatureFlags === false
}

export const identifyUser = (user) => {
  if (!isReady() || !user || user.id == null) return
  posthog.identify(String(user.id), {
    email: user.email,
    role: user.role
  })
}

// SPEC-075: tag every event with the release it came from. Prefer the id the
// source-map step injects at the top of each bundle (window._posthogReleaseId,
// set before any app code runs); builds without injection (no key, CLI
// failure, local) fall back to the commit baked in at build time.
// release_version is always the human-readable swsca-frontend@<sha>.
export const releaseProperties = (
  win = typeof window !== 'undefined' ? window : {},
  buildSha = typeof BUILD_SHA !== 'undefined' ? BUILD_SHA : ''
) => {
  const version = buildSha ? `swsca-frontend@${buildSha}` : undefined
  // eslint-disable-next-line no-underscore-dangle
  const release = (win && win._posthogReleaseId) || version
  if (!release) return null
  return version ? { release, release_version: version } : { release }
}

export const registerRelease = () => {
  if (!isReady()) return
  const properties = releaseProperties()
  if (properties) posthog.register(properties)
}

export const resetPosthog = () => {
  if (!isReady()) return
  posthog.reset()
}

// Generic event capture. Mirrors pushGa4Event's role as the single chokepoint,
// but for PostHog: given a resolved event name and a flat properties bag it
// forwards to posthog.capture, no-opping until PostHog is initialized.
export const capturePosthogEvent = (name, properties = {}) => {
  if (!isReady() || !name) return
  posthog.capture(name, properties)
}

// Feature flags. All guarded so they return safe defaults until PostHog loads.
export const isFeatureEnabled = (flag) =>
  isReady() ? posthog.isFeatureEnabled(flag) : false

export const getFeatureFlag = (flag) =>
  isReady() ? posthog.getFeatureFlag(flag) : undefined

// Subscribe to flag load/refresh. Returns an unsubscribe function (a no-op when
// PostHog is not initialized) so React effects can clean up uniformly.
export const onFeatureFlags = (callback) =>
  isReady() ? posthog.onFeatureFlags(callback) : () => {}
